import React, { useState } from 'react';
import { Form, Button, Row, Col, Card, Spinner, Alert, Modal, ListGroup } from 'react-bootstrap';
import { FaMoneyBillWave, FaPlus, FaTrash, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';
import api from '../services/api';

const DMTBank2Modal = ({ show, onHide, onSuccess }) => {
  const [currentStep, setCurrentStep] = useState(1); // 1: Query Remitter, 2: Aadhar Verify, 3: Register, 4: Add Beneficiary, 5: Transaction
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Form Data States
  const [remitterData, setRemitterData] = useState({
    mobile: '',
    aadhar_number: '',
    otp: '',
    first_name: '',
    last_name: '',
    pincode: '',
    address: '',
    city: '',
    state: ''
  });

  const [beneficiaryData, setBeneficiaryData] = useState({
    mobile: '',
    bene_name: '',
    bene_account: '',
    bene_ifsc: '',
    bene_bank: '',
    bene_mobile: ''
  });

  const [transactionData, setTransactionData] = useState({
    mobile: '',
    bene_id: '',
    amount: '',
    reference_id: '',
    otp: '',
    mode: 'IMPS'
  });

  const [refundData, setRefundData] = useState({
    reference_id: '',
    otp: ''
  });

  const [statusData, setStatusData] = useState({
    reference_id: ''
  });

  const [transactionStatus, setTransactionStatus] = useState(null);

  // State Management
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [selectedBeneficiary, setSelectedBeneficiary] = useState(null);

  // Quick amounts for transaction
  const quickAmounts = [100, 200, 500, 1000, 2000, 5000, 10000, 25000];

  // Step 1: Query Remitter
  const handleQueryRemitter = async () => {
    if (!remitterData.mobile) {
      setError('Please enter mobile number');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api.post('/api/dmt-bank2/remitter/query', {
        mobile: remitterData.mobile
      });

      if (response.data.success) {
        if (response.data.data?.response_code === 1) {
          setSuccess('Remitter found! You can proceed to add beneficiaries and make transactions.');
          setCurrentStep(4); // Skip to beneficiary management
          await fetchBeneficiaries();
        } else if (response.data.data?.response_code === 2) {
          setSuccess('Please validate your aadhaar number to continue.');
          setCurrentStep(2); // Go to Aadhar verification
        } else {
          setSuccess('Remitter not found. Please complete registration process.');
          setCurrentStep(2); // Go to Aadhar verification
        }
      } else {
        setError(response.data.message || 'Query remitter failed');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Query remitter failed');
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Aadhar Verify
  const handleAadharVerify = async () => {
    if (!remitterData.mobile || !remitterData.aadhar_number) {
      setError('Please enter mobile number and Aadhar number');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api.post('/api/dmt-bank2/remitter/aadhar-verify', {
        mobile: remitterData.mobile,
        aadhar_number: remitterData.aadhar_number
      });

      if (response.data.success) {
        // Save stateresp from response for registration
        console.log('Aadhaar verification response:', response.data);
        if (response.data.data?.stateresp) {
          console.log('Found stateresp:', response.data.data.stateresp);
          setRemitterData(prev => ({
            ...prev,
            stateresp: response.data.data.stateresp
          }));
        } else {
          console.error('No stateresp in response!', response.data);
        }
        setSuccess('Aadhar verified successfully! OTP sent to your mobile. Please complete registration.');
        setCurrentStep(3); // Go to registration
      } else {
        setError(response.data.message || 'Aadhar verification failed');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Aadhar verification failed');
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Register Remitter
  const handleRegisterRemitter = async () => {
    if (!remitterData.mobile || !remitterData.otp || !remitterData.stateresp) {
      setError('Please fill all required fields. Missing: ' + 
        (!remitterData.mobile ? 'mobile ' : '') + 
        (!remitterData.otp ? 'otp ' : '') + 
        (!remitterData.stateresp ? 'stateresp' : ''));
      return;
    }

    setLoading(true);
    setError('');

    try {
      const payload = {
        mobile: remitterData.mobile,
        otp: remitterData.otp,
        stateresp: remitterData.stateresp
      };
      console.log('Sending registration payload:', payload);
      const response = await api.post('/api/dmt-bank2/remitter/register', payload);
      console.log('Remitter registration response:', response.data);

      if (response.data.success) {
        setSuccess('Remitter registered successfully! You can now add beneficiaries and make transactions.');
        setCurrentStep(4); // Go to beneficiary management
        await fetchBeneficiaries();
      } else {
        setError(response.data.message || 'Remitter registration failed');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Remitter registration failed');
    } finally {
      setLoading(false);
    }
  };

  // Step 4: Add Beneficiary
  const handleAddBeneficiary = async () => {
    if (!beneficiaryData.bene_name || !beneficiaryData.bene_account || !beneficiaryData.bene_ifsc || !beneficiaryData.bene_bank) {
      setError('Please fill all required fields including Bank Name');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Convert bank name to numeric bank ID for PaySprint
      const getBankId = (bankName) => {
        const bankMap = {
          'hdfc': '1',
          'sbi': '2', 
          'icici': '3',
          'axis': '4',
          'kotak': '5',
          'pnb': '6',
          'bob': '7',
          'canara': '8',
          'union': '9',
          'indian': '10'
        };
        return bankMap[bankName?.toLowerCase()] || '1'; // Default to HDFC
      };

      const response = await api.post('/api/dmt-bank2/beneficiary/register', {
        mobile: remitterData.mobile, // Use remitter's mobile
        benename: beneficiaryData.bene_name,  // PaySprint field name
        accno: beneficiaryData.bene_account,   // PaySprint field name
        bankid: getBankId(beneficiaryData.bene_bank),    // Convert to numeric ID
        ifsccode: beneficiaryData.bene_ifsc,  // PaySprint field name
        verified: '0'  // Default to not verified
      });

      console.log('Beneficiary registration response:', response.data);
      
      if (response.data.success) {
        setSuccess('Beneficiary added successfully!');
        setError(''); // Clear any previous errors
        // Reset beneficiary form completely
        setBeneficiaryData({
          mobile: remitterData.mobile,
          bene_name: '',
          bene_account: '',
          bene_ifsc: '',
          bene_bank: '',
          bene_mobile: ''
        });
        console.log('Calling fetchBeneficiaries after successful addition...');
        await fetchBeneficiaries();
      } else {
        setError(response.data.message || 'Failed to add beneficiary');
        setSuccess(''); // Clear success message on error
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add beneficiary');
    } finally {
      setLoading(false);
    }
  };

  // Fetch Beneficiaries
  const fetchBeneficiaries = async () => {
    try {
      console.log('Fetching beneficiaries for mobile:', remitterData.mobile);
      const response = await api.post('/api/dmt-bank2/beneficiary/fetch', {
        mobile: remitterData.mobile
      });

      console.log('Fetch beneficiaries response:', response.data);
      console.log('Response data structure:', Object.keys(response.data));
      console.log('Data object:', response.data.data);
      console.log('Data object keys:', response.data.data ? Object.keys(response.data.data) : 'no data object');
      
      if (response.data.success) {
        // Try multiple possible structures
        let beneficiaries = [];
        
        // Structure 1: response.data.data.beneficiaries
        if (response.data.data?.beneficiaries) {
          beneficiaries = response.data.data.beneficiaries;
          console.log('Found beneficiaries in data.beneficiaries:', beneficiaries);
        }
        // Structure 2: response.data.data (direct array)
        else if (Array.isArray(response.data.data)) {
          beneficiaries = response.data.data;
          console.log('Found beneficiaries as direct data array:', beneficiaries);
        }
        // Structure 3: response.data.beneficiaries (direct in response)
        else if (response.data.beneficiaries) {
          beneficiaries = response.data.beneficiaries;
          console.log('Found beneficiaries in response.beneficiaries:', beneficiaries);
        }
        // Structure 4: response.data.data as object with array values
        else if (response.data.data && typeof response.data.data === 'object') {
          // Look for any array in the data object
          const dataObj = response.data.data;
          for (const key in dataObj) {
            if (Array.isArray(dataObj[key])) {
              beneficiaries = dataObj[key];
              console.log(`Found beneficiaries in data.${key}:`, beneficiaries);
              break;
            }
          }
        }
        
        console.log('Final beneficiaries array:', beneficiaries);
        setBeneficiaries(beneficiaries);
      } else {
        console.log('Fetch beneficiaries failed:', response.data.message);
      }
    } catch (err) {
      console.error('Failed to fetch beneficiaries:', err);
    }
  };

  // Delete Beneficiary
  const handleDeleteBeneficiary = async (beneId) => {
    if (!window.confirm('Are you sure you want to delete this beneficiary?')) return;

    setLoading(true);
    setError('');

    try {
      const response = await api.post('/api/dmt-bank2/beneficiary/delete', {
        mobile: remitterData.mobile,
        bene_id: beneId
      });

      if (response.data.success) {
        setSuccess('Beneficiary deleted successfully!');
        await fetchBeneficiaries();
      } else {
        setError(response.data.message || 'Failed to delete beneficiary');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete beneficiary');
    } finally {
      setLoading(false);
    }
  };

  // Fetch Beneficiary by ID
  const fetchBeneficiaryById = async (beneId) => {
    try {
      const response = await api.post('/api/dmt-bank2/beneficiary/fetch-by-id', {
        mobile: remitterData.mobile,
        bene_id: beneId
      });

      if (response.data.success) {
        return response.data.data;
      }
      return null;
    } catch (err) {
      console.error('Failed to fetch beneficiary details:', err);
      return null;
    }
  };

  // Step 5: Penny Drop
  const handlePennyDrop = async () => {
    if (!selectedBeneficiary) {
      setError('Please select a beneficiary first');
      return;
    }

    setLoading(true);
    setError('');

    const referenceId = 'PENNY_' + Date.now();

    try {
      const response = await api.post('/api/dmt-bank2/transaction/penny-drop', {
        mobile: remitterData.mobile,
        bene_id: selectedBeneficiary.bene_id,
        reference_id: referenceId
      });

      if (response.data.success) {
        setSuccess('Penny drop successful! Account verified. You can now proceed with the transaction.');
        setCurrentStep(6); // Go to transaction
      } else {
        setError(response.data.message || 'Penny drop failed');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Penny drop failed');
    } finally {
      setLoading(false);
    }
  };

  // Step 6: Send Transaction OTP
  const handleSendTransactionOTP = async () => {
    if (!selectedBeneficiary || !transactionData.amount) {
      setError('Please select beneficiary and enter amount');
      return;
    }

    setLoading(true);
    setError('');

    const referenceId = 'TXN_' + Date.now();
    setTransactionData(prev => ({ ...prev, reference_id: referenceId }));

    try {
      const response = await api.post('/api/dmt-bank2/transaction/send-otp', {
        mobile: remitterData.mobile,
        bene_id: selectedBeneficiary.bene_id,
        amount: transactionData.amount,
        reference_id: referenceId,
        txntype: transactionData.mode || 'IMPS', // Transaction type (IMPS/NEFT)
        mode: transactionData.mode || 'IMPS', // Alternative field name
        pincode: remitterData.pincode || '',
        address: remitterData.address || '',
        dob: remitterData.dob || '',
        gst_state: remitterData.gst_state || ''
      });

      if (response.data.success) {
        const psData = response.data.data || {};
        const stateresp =
          psData.stateresp ||
          psData.data?.stateresp ||
          null;

        // Store OTP reference number (stateresp) for processTransaction
        if (stateresp) {
          setTransactionData(prev => ({ ...prev, stateresp }));
        }

        setSuccess('Transaction OTP sent successfully! Please enter OTP to complete transaction.');
        setCurrentStep(7); // Go to transaction processing
      } else {
        setError(response.data.message || 'Failed to send transaction OTP');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send transaction OTP');
    } finally {
      setLoading(false);
    }
  };

  // Step 7: Process Transaction
  const handleProcessTransaction = async () => {
    if (!transactionData.otp) {
      setError('Please enter OTP');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api.post('/api/dmt-bank2/transaction/process', {
        mobile: remitterData.mobile,
        bene_id: selectedBeneficiary.bene_id,
        amount: transactionData.amount,
        reference_id: transactionData.reference_id,
        otp: transactionData.otp,
        mode: transactionData.mode,
        stateresp: transactionData.stateresp
      });

      if (response.data.success) {
        // Normalize transaction data and ID fields from PaySprint
        const txnData = response.data.data || {};
        const txnStatus = response.data.txn_status || txnData.txn_status || 'processing';
        const txnId =
          txnData.transaction_id ||
          txnData.ackno ||
          txnData.referenceid ||
          txnData.utr ||
          txnData.ref_id ||
          transactionData.reference_id ||
          'N/A';

        let statusMessage = '';
        switch (txnStatus) {
          case 'success':
            statusMessage = `Transaction successful! Money credited to beneficiary account. Transaction ID: ${txnId}`;
            break;
          case 'failed':
            statusMessage = `Transaction failed. ${response.data.message || 'Please try again or contact support.'}`;
            // Add refund option for failed transactions
            setTimeout(() => {
              if (window.confirm('Transaction failed. Would you like to process a refund?')) {
                handleSendRefundOTP(transactionData.reference_id);
              }
            }, 2000);
            break;
          case 'hold':
            statusMessage = `Transaction is on hold. This may be due to bank node issues. Please check status later. Transaction ID: ${txnId}`;
            break;
          case 'processing':
            statusMessage = `Transaction is processing. Please check status in a few minutes. Transaction ID: ${txnId}`;
            break;
          default:
            statusMessage = `Transaction status: ${txnStatus}. Transaction ID: ${txnId}`;
        }

        if (txnStatus === 'success') {
          setSuccess(statusMessage);
          onSuccess?.({ ...txnData, transaction_id: txnId, txn_status: txnStatus });
        } else {
          setError(statusMessage);
        }
        setCurrentStep(8); // Show final status
      } else {
        setError(response.data.message || 'Transaction failed');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Transaction failed');
    } finally {
      setLoading(false);
    }
  };

  // Send Refund OTP
  const handleSendRefundOTP = async (referenceId) => {
    setLoading(true);
    setError('');

    try {
      const response = await api.post('/api/dmt-bank2/refund/send-otp', {
        reference_id: referenceId
      });

      if (response.data.success) {
        setRefundData({ reference_id: referenceId, otp: '' });
        setSuccess('Refund OTP sent successfully! Please enter OTP to process refund.');
        setCurrentStep(9); // Go to refund processing
      } else {
        setError(response.data.message || 'Failed to send refund OTP');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send refund OTP');
    } finally {
      setLoading(false);
    }
  };

  // Process Refund
  const handleProcessRefund = async () => {
    if (!refundData.otp) {
      setError('Please enter refund OTP');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api.post('/api/dmt-bank2/refund/claim', {
        reference_id: refundData.reference_id,
        otp: refundData.otp
      });

      if (response.data.success) {
        setSuccess('Refund processed successfully! Amount will be credited back to your wallet.');
        setCurrentStep(8); // Go back to final status
      } else {
        setError(response.data.message || 'Refund processing failed');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Refund processing failed');
    } finally {
      setLoading(false);
    }
  };

  // Check Transaction Status
  const handleCheckTransactionStatus = async () => {
    if (!statusData.reference_id) {
      setError('Please enter reference ID');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await api.post('/api/dmt-bank2/transaction/status', {
        reference_id: statusData.reference_id
      });

      if (response.data.success) {
        setTransactionStatus(response.data.data);
        const statusMsg = response.data.data?.message || 'Transaction status fetched successfully';
        setSuccess(statusMsg);
      } else {
        setError(response.data.message || 'Failed to fetch transaction status');
        setTransactionStatus(null);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch transaction status');
      setTransactionStatus(null);
    } finally {
      setLoading(false);
    }
  };

  // Reset form
  const handleReset = () => {
    setCurrentStep(1);
    setError('');
    setSuccess('');
    setTransactionStatus(null);
    setRemitterData({
      mobile: '',
      aadhar_number: '',
      otp: '',
      first_name: '',
      last_name: '',
      pincode: '',
      address: '',
      city: '',
      state: ''
    });
    setBeneficiaryData({
      mobile: '',
      bene_name: '',
      bene_account: '',
      bene_ifsc: '',
      bene_bank: '',
      bene_mobile: ''
    });
    setTransactionData({
      mobile: '',
      bene_id: '',
      amount: '',
      reference_id: '',
      otp: '',
      mode: 'IMPS'
    });
    setStatusData({
      reference_id: ''
    });
    setRefundData({
      reference_id: '',
      otp: ''
    });
    setBeneficiaries([]);
    setSelectedBeneficiary(null);
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <FaMoneyBillWave className="me-2" />
          DMT Bank2 Service
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}

        {/* Step 1: Query Remitter */}
        {currentStep === 1 && (
          <Card>
            <Card.Header>
              <h5 className="mb-0">Step 1: Query Remitter</h5>
            </Card.Header>
            <Card.Body>
              <Form.Group className="mb-3">
                <Form.Label>Mobile Number *</Form.Label>
                <Form.Control
                  type="text"
                  value={remitterData.mobile}
                  onChange={(e) => setRemitterData(prev => ({ ...prev, mobile: e.target.value }))}
                  placeholder="Enter 10-digit mobile number"
                  maxLength={10}
                />
              </Form.Group>

              <Button onClick={handleQueryRemitter} disabled={loading} className="w-100">
                {loading ? <Spinner size="sm" className="me-2" /> : null}
                Query Remitter
              </Button>
            </Card.Body>
          </Card>
        )}

        {/* Step 2: Aadhar Verify */}
        {currentStep === 2 && (
          <Card>
            <Card.Header>
              <h5 className="mb-0">Step 2: Aadhar Verification</h5>
            </Card.Header>
            <Card.Body>
              <Form.Group className="mb-3">
                <Form.Label>Mobile Number *</Form.Label>
                <Form.Control
                  type="text"
                  value={remitterData.mobile}
                  onChange={(e) => setRemitterData(prev => ({ ...prev, mobile: e.target.value }))}
                  placeholder="Enter 10-digit mobile number"
                  maxLength={10}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Aadhar Number *</Form.Label>
                <Form.Control
                  type="text"
                  value={remitterData.aadhar_number}
                  onChange={(e) => {
                    // Only allow digits
                    const value = e.target.value.replace(/\D/g, '');
                    setRemitterData(prev => ({ ...prev, aadhar_number: value }));
                  }}
                  placeholder="Enter 12-digit Aadhar number"
                  maxLength={16}
                />
                <Form.Text className="text-muted">
                  For UAT testing, use: 222244442222 (12 digits) | OTP: 123456
                </Form.Text>
              </Form.Group>

              <div className="d-flex gap-2">
                <Button 
                  variant="outline-secondary" 
                  onClick={() => setCurrentStep(1)}
                  className="flex-fill"
                >
                  Back
                </Button>
                <Button onClick={handleAadharVerify} disabled={loading} className="flex-fill">
                  {loading ? <Spinner size="sm" className="me-2" /> : null}
                  Verify Aadhar
                </Button>
              </div>
            </Card.Body>
          </Card>
        )}

        {/* Step 3: Register Remitter */}
        {currentStep === 3 && (
          <Card>
            <Card.Header>
              <h5 className="mb-0">Step 3: Register Remitter</h5>
            </Card.Header>
            <Card.Body>
              <div className="mb-3 p-3 bg-light rounded">
                <strong>Registration Details:</strong><br />
                <strong>Mobile:</strong> {remitterData.mobile}<br />
                <strong>Aadhaar:</strong> {remitterData.aadhar_number}<br />
                <strong>Status:</strong> Aadhaar verified, OTP sent
              </div>

              <Form.Group className="mb-3">
                <Form.Label>Enter OTP *</Form.Label>
                <Form.Control
                  type="text"
                  value={remitterData.otp}
                  onChange={(e) => setRemitterData(prev => ({ ...prev, otp: e.target.value }))}
                  placeholder="Enter 6-digit OTP"
                  maxLength={6}
                />
                <Form.Text className="text-muted">
                  OTP sent to your mobile number for registration.
                </Form.Text>
              </Form.Group>

              <div className="d-flex gap-2">
                <Button 
                  variant="outline-secondary" 
                  onClick={() => setCurrentStep(2)}
                  className="flex-fill"
                >
                  Back
                </Button>
                <Button onClick={handleRegisterRemitter} disabled={loading} className="flex-fill">
                  {loading ? <Spinner size="sm" className="me-2" /> : null}
                  Register Remitter
                </Button>
              </div>
            </Card.Body>
          </Card>
        )}

        {/* Step 4: Add Beneficiary */}
        {currentStep === 4 && (
          <Card>
            <Card.Header>
              <h5 className="mb-0">Step 4: Manage Beneficiaries</h5>
            </Card.Header>
            <Card.Body>
              <div className="mb-4">
                <h6>Add New Beneficiary</h6>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Beneficiary Name *</Form.Label>
                      <Form.Control
                        type="text"
                        value={beneficiaryData.bene_name}
                        onChange={(e) => setBeneficiaryData(prev => ({ ...prev, bene_name: e.target.value }))}
                        placeholder="Enter beneficiary name"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Account Number *</Form.Label>
                      <Form.Control
                        type="text"
                        value={beneficiaryData.bene_account}
                        onChange={(e) => setBeneficiaryData(prev => ({ ...prev, bene_account: e.target.value }))}
                        placeholder="Enter account number"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>IFSC Code *</Form.Label>
                      <Form.Control
                        type="text"
                        value={beneficiaryData.bene_ifsc}
                        onChange={(e) => setBeneficiaryData(prev => ({ ...prev, bene_ifsc: e.target.value.toUpperCase() }))}
                        placeholder="Enter IFSC code"
                        style={{ textTransform: 'uppercase' }}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Bank Name *</Form.Label>
                      <Form.Control
                        type="text"
                        value={beneficiaryData.bene_bank}
                        onChange={(e) => setBeneficiaryData(prev => ({ ...prev, bene_bank: e.target.value }))}
                        placeholder="Enter bank name (e.g., hdfc, sbi, icici)"
                        required
                      />
                      <Form.Text className="text-muted">
                        Common banks: hdfc, sbi, icici, axis, kotak, pnb
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                <Button onClick={handleAddBeneficiary} disabled={loading} className="w-100">
                  {loading ? <Spinner size="sm" className="me-2" /> : null}
                  Add Beneficiary
                </Button>
              </div>

              <div className="mb-4">
                <h6>Existing Beneficiaries</h6>
                {beneficiaries.length > 0 ? (
                  <ListGroup>
                    {beneficiaries.map((beneficiary, index) => (
                      <ListGroup.Item key={index} className="d-flex justify-content-between align-items-center">
                        <div>
                          <strong>{beneficiary.bene_name}</strong><br />
                          <small className="text-muted">
                            {beneficiary.bene_account} | {beneficiary.bene_ifsc}
                          </small>
                        </div>
                        <div>
                          <Button
                            variant="outline-info"
                            size="sm"
                            className="me-2"
                            onClick={async () => {
                              const details = await fetchBeneficiaryById(beneficiary.bene_id);
                              if (details) {
                                alert(`Beneficiary Details:\nName: ${details.bene_name}\nAccount: ${details.bene_account}\nIFSC: ${details.bene_ifsc}\nBank: ${details.bene_bank || 'N/A'}`);
                              }
                            }}
                          >
                            View Details
                          </Button>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="me-2"
                            onClick={() => {
                              setSelectedBeneficiary(beneficiary);
                              setCurrentStep(5);
                            }}
                          >
                            Select
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDeleteBeneficiary(beneficiary.bene_id)}
                          >
                            <FaTrash />
                          </Button>
                        </div>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                ) : (
                  <p className="text-muted">No beneficiaries added yet.</p>
                )}
              </div>

              <Button 
                variant="outline-secondary" 
                onClick={() => setCurrentStep(1)}
                className="w-100"
              >
                Back to Query Remitter
              </Button>
            </Card.Body>
          </Card>
        )}

        {/* Step 5: Penny Drop Verification */}
        {currentStep === 5 && selectedBeneficiary && (
          <Card>
            <Card.Header>
              <h5 className="mb-0">Step 5: Verify Beneficiary Account</h5>
            </Card.Header>
            <Card.Body>
              <div className="mb-3 p-3 bg-light rounded">
                <strong>Selected Beneficiary:</strong><br />
                {selectedBeneficiary.bene_name} | {selectedBeneficiary.bene_account} | {selectedBeneficiary.bene_ifsc}
              </div>

              <div className="alert alert-info">
                <strong>Penny Drop Verification:</strong><br />
                This will verify the beneficiary account by crediting ₹1 to their account. 
                This helps ensure the account details are correct before making the actual transaction.
              </div>

              <Button onClick={handlePennyDrop} disabled={loading} className="w-100">
                {loading ? <Spinner size="sm" className="me-2" /> : null}
                Verify Account (Penny Drop)
              </Button>
            </Card.Body>
          </Card>
        )}

        {/* Step 6: Transaction Amount & OTP */}
        {currentStep === 6 && selectedBeneficiary && (
          <Card>
            <Card.Header>
              <h5 className="mb-0">Step 6: Transaction Details</h5>
            </Card.Header>
            <Card.Body>
              <div className="mb-3 p-3 bg-light rounded">
                <strong>Selected Beneficiary:</strong><br />
                {selectedBeneficiary.bene_name} | {selectedBeneficiary.bene_account} | {selectedBeneficiary.bene_ifsc}
              </div>

              <Form.Group className="mb-3">
                <Form.Label>Amount *</Form.Label>
                <Form.Control
                  type="number"
                  value={transactionData.amount}
                  onChange={(e) => setTransactionData(prev => ({ ...prev, amount: e.target.value }))}
                  placeholder="Enter amount"
                  min="1"
                />
              </Form.Group>

              <div className="mb-3">
                <Form.Label>Quick Amounts:</Form.Label>
                <div className="d-flex flex-wrap gap-2">
                  {quickAmounts.map((amount) => (
                    <Button
                      key={amount}
                      variant="outline-primary"
                      size="sm"
                      onClick={() => setTransactionData(prev => ({ ...prev, amount: amount.toString() }))}
                    >
                      ₹{amount}
                    </Button>
                  ))}
                </div>
              </div>

              <Form.Group className="mb-3">
                <Form.Label>Transaction Mode</Form.Label>
                <Form.Select
                  value={transactionData.mode}
                  onChange={(e) => setTransactionData(prev => ({ ...prev, mode: e.target.value }))}
                >
                  <option value="IMPS">IMPS</option>
                  <option value="NEFT">NEFT</option>
                  <option value="RTGS">RTGS</option>
                </Form.Select>
              </Form.Group>

              <Button onClick={handleSendTransactionOTP} disabled={loading} className="w-100">
                {loading ? <Spinner size="sm" className="me-2" /> : null}
                Send Transaction OTP
              </Button>
            </Card.Body>
          </Card>
        )}

        {/* Step 7: Process Transaction */}
        {currentStep === 7 && selectedBeneficiary && (
          <Card>
            <Card.Header>
              <h5 className="mb-0">Step 7: Process Transaction</h5>
            </Card.Header>
            <Card.Body>
              <div className="mb-3 p-3 bg-light rounded">
                <strong>Transaction Details:</strong><br />
                <strong>Beneficiary:</strong> {selectedBeneficiary.bene_name}<br />
                <strong>Amount:</strong> ₹{transactionData.amount}<br />
                <strong>Mode:</strong> {transactionData.mode}<br />
                <strong>Reference ID:</strong> {transactionData.reference_id}
              </div>

              <Form.Group className="mb-3">
                <Form.Label>Enter Transaction OTP *</Form.Label>
                <Form.Control
                  type="text"
                  value={transactionData.otp}
                  onChange={(e) => setTransactionData(prev => ({ ...prev, otp: e.target.value }))}
                  placeholder="Enter 6-digit OTP"
                  maxLength={6}
                />
                <Form.Text className="text-muted">
                  OTP sent to your mobile number for transaction processing.
                </Form.Text>
              </Form.Group>

              <Button onClick={handleProcessTransaction} disabled={loading} className="w-100 mb-2">
                {loading ? <Spinner size="sm" className="me-2" /> : null}
                Process Transaction
              </Button>

              {transactionData.reference_id && (
                <Button 
                  onClick={() => {
                    setStatusData({ reference_id: transactionData.reference_id });
                    setCurrentStep(10);
                  }} 
                  variant="outline-info" 
                  className="w-100"
                  disabled={loading}
                >
                  Check Transaction Status
                </Button>
              )}
            </Card.Body>
          </Card>
        )}

        {/* Step 8: Final Status */}
        {currentStep === 8 && (
          <Card>
            <Card.Header>
              <h5 className="mb-0">Transaction Status</h5>
            </Card.Header>
            <Card.Body>
              <div className="text-center">
                <FaCheckCircle className="text-success mb-3" style={{ fontSize: '3rem' }} />
                <h5>Transaction Process Complete</h5>
                <p className="text-muted">
                  Your transaction has been processed. Please check the status above for details.
                </p>
                <div className="d-flex gap-2 justify-content-center">
                  <Button onClick={() => setCurrentStep(10)} variant="outline-info">
                    Check Transaction Status
                  </Button>
                  <Button onClick={handleReset} variant="primary">
                    Start New Transaction
                  </Button>
                </div>
              </div>
            </Card.Body>
          </Card>
        )}

        {/* Step 10: Check Transaction Status */}
        {currentStep === 10 && (
          <Card>
            <Card.Header>
              <h5 className="mb-0">Check Transaction Status</h5>
            </Card.Header>
            <Card.Body>
              <Form.Group className="mb-3">
                <Form.Label>Reference ID *</Form.Label>
                <Form.Control
                  type="text"
                  value={statusData.reference_id}
                  onChange={(e) => setStatusData(prev => ({ ...prev, reference_id: e.target.value }))}
                  placeholder="Enter transaction reference ID"
                />
                <Form.Text className="text-muted">
                  Enter the reference ID from your transaction to check its current status.
                </Form.Text>
              </Form.Group>

              <Button onClick={handleCheckTransactionStatus} disabled={loading} className="w-100 mb-3">
                {loading ? <Spinner size="sm" className="me-2" /> : null}
                Check Status
              </Button>

              {transactionStatus && (
                <Card className="mt-3">
                  <Card.Header>
                    <h6 className="mb-0">Transaction Details</h6>
                  </Card.Header>
                  <Card.Body>
                    <ListGroup variant="flush">
                      {transactionStatus.response_code && (
                        <ListGroup.Item>
                          <strong>Response Code:</strong> {transactionStatus.response_code}
                        </ListGroup.Item>
                      )}
                      {transactionStatus.status && (
                        <ListGroup.Item>
                          <strong>Status:</strong> {transactionStatus.status}
                        </ListGroup.Item>
                      )}
                      {transactionStatus.txn_status !== undefined && (
                        <ListGroup.Item>
                          <strong>Transaction Status:</strong> {transactionStatus.txn_status}
                        </ListGroup.Item>
                      )}
                      {transactionStatus.utr && (
                        <ListGroup.Item>
                          <strong>UTR:</strong> {transactionStatus.utr}
                        </ListGroup.Item>
                      )}
                      {transactionStatus.ackno && (
                        <ListGroup.Item>
                          <strong>Acknowledgment Number:</strong> {transactionStatus.ackno}
                        </ListGroup.Item>
                      )}
                      {transactionStatus.benename && (
                        <ListGroup.Item>
                          <strong>Beneficiary Name:</strong> {transactionStatus.benename}
                        </ListGroup.Item>
                      )}
                      {transactionStatus.amount && (
                        <ListGroup.Item>
                          <strong>Amount:</strong> ₹{transactionStatus.amount}
                        </ListGroup.Item>
                      )}
                      {transactionStatus.balance !== undefined && (
                        <ListGroup.Item>
                          <strong>Balance:</strong> ₹{transactionStatus.balance}
                        </ListGroup.Item>
                      )}
                      {transactionStatus.message && (
                        <ListGroup.Item>
                          <strong>Message:</strong> {transactionStatus.message}
                        </ListGroup.Item>
                      )}
                      {transactionStatus.data && typeof transactionStatus.data === 'object' && (
                        <ListGroup.Item>
                          <strong>Additional Data:</strong>
                          <pre className="mt-2 mb-0" style={{ fontSize: '0.85rem' }}>
                            {JSON.stringify(transactionStatus.data, null, 2)}
                          </pre>
                        </ListGroup.Item>
                      )}
                    </ListGroup>
                  </Card.Body>
                </Card>
              )}

              <div className="mt-3">
                <Button onClick={() => setCurrentStep(8)} variant="secondary" className="me-2">
                  Back
                </Button>
                <Button onClick={handleReset} variant="primary">
                  Start New Transaction
                </Button>
              </div>
            </Card.Body>
          </Card>
        )}

        {/* Step 9: Refund Confirmation */}
        {currentStep === 9 && (
          <Card>
            <Card.Header>
              <h5 className="mb-0">Step 9: Process Refund</h5>
            </Card.Header>
            <Card.Body>
              <div className="mb-3 p-3 bg-light rounded">
                <strong>Refund Details:</strong><br />
                <strong>Reference ID:</strong> {refundData.reference_id}<br />
                <strong>Status:</strong> Failed Transaction - Refund Available
              </div>

              <div className="alert alert-info">
                <strong>Refund Information:</strong><br />
                This transaction failed and is eligible for refund. The amount will be credited back to your wallet along with all charges.
              </div>

              <Form.Group className="mb-3">
                <Form.Label>Enter Refund OTP *</Form.Label>
                <Form.Control
                  type="text"
                  value={refundData.otp}
                  onChange={(e) => setRefundData(prev => ({ ...prev, otp: e.target.value }))}
                  placeholder="Enter 6-digit OTP"
                  maxLength={6}
                />
                <Form.Text className="text-muted">
                  OTP sent to your mobile number for refund processing.
                </Form.Text>
              </Form.Group>

              <Button onClick={handleProcessRefund} disabled={loading} className="w-100">
                {loading ? <Spinner size="sm" className="me-2" /> : null}
                Process Refund
              </Button>
            </Card.Body>
          </Card>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
        <Button variant="outline-warning" onClick={handleReset}>
          Reset
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DMTBank2Modal;
